import React from 'react';
import {
  Button,
  Card,
  Descriptions,
  Tag,
  Space,
  Typography,
  Spin,
  Popconfirm,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { zaloZnsTemplateService } from '../../services/zalo-zns-template.service';
import { useZaloZnsTemplateDetail } from '../../hooks/useZaloZnsTemplates';

const { Title, Text } = Typography;

export const ZaloZnsTemplateView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { template, loading } = useZaloZnsTemplateDetail(id);

  const handleClone = async () => {
    try {
      const cloned = await zaloZnsTemplateService.clone(id!);
      message.success(`Đã nhân bản thành: ${cloned.code}`);
      navigate(`/zalo-zns-templates/${cloned._id}/edit`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Nhân bản thất bại');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!template) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Title level={4}>Template không tồn tại</Title>
        <Button onClick={() => navigate('/zalo-zns-templates')}>Quay lại danh sách</Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Chi tiết template Zalo ZNS
          </Title>
          <Text type="secondary">{template.name}</Text>
        </div>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/zalo-zns-templates')}>
            Quay lại
          </Button>
          <Popconfirm
            title="Nhân bản template này?"
            description={`Sẽ tạo bản copy với code: copy-${template.code}`}
            onConfirm={handleClone}
            okText="Nhân bản"
            cancelText="Hủy"
          >
            <Button icon={<CopyOutlined />}>Nhân bản</Button>
          </Popconfirm>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/zalo-zns-templates/${id}/edit`)}
          >
            Chỉnh sửa
          </Button>
        </Space>
      </div>

      <Card title="Thông tin cơ bản" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
          <Descriptions.Item label="Tên template">{template.name}</Descriptions.Item>
          <Descriptions.Item label="Code">
            <code style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: 3 }}>
              {template.code}
            </code>
          </Descriptions.Item>
          <Descriptions.Item label="Mã ZNS template">
            <Tag color="purple">{template.template_id}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Trạng thái">
            <Tag color={template.state === 1 ? 'success' : 'error'}>
              {template.state === 1 ? 'Hoạt động' : 'Tắt'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Ngày tạo">
            {dayjs(template.created_at).format('DD/MM/YYYY HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label="Cập nhật lần cuối">
            {dayjs(template.updated_at).format('DD/MM/YYYY HH:mm:ss')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Nội dung" style={{ marginBottom: 16 }}>
        <div
          style={{
            background: '#f8f8f8',
            border: '1px solid #e8e8e8',
            borderRadius: 6,
            padding: 16,
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: 13,
            lineHeight: 1.8,
          }}
        >
          {template.content}
        </div>
      </Card>

      {template.params && template.params.length > 0 && (
        <Card title={`Tham số (${template.params.length})`}>
          <Space wrap>
            {template.params.map((param, index) => (
              <Tag key={index} color="geekblue" style={{ fontSize: 13, padding: '4px 10px' }}>
                {param}
              </Tag>
            ))}
          </Space>
          <p style={{ color: '#8c8c8c', fontSize: 12, marginTop: 8, marginBottom: 0 }}>
            Các tham số này được khai báo trong nội dung template bằng cú pháp{' '}
            <code>{'<tên_tham_số>'}</code>.
          </p>
        </Card>
      )}
    </div>
  );
};

export default ZaloZnsTemplateView;
